import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('nutriquimia-token'));
  const [loading, setLoading] = useState(true);
  const justLoggedIn = useRef(false);
  const interceptorId = useRef(null);

  // Setup axios 401 interceptor once
  useEffect(() => {
    interceptorId.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
          localStorage.removeItem('nutriquimia-token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorId.current !== null) {
        axios.interceptors.response.eject(interceptorId.current);
      }
    };
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only logout if it's a 401 (invalid/expired token), not a network error
      if (error.response?.status === 401) {
        localStorage.removeItem('nutriquimia-token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
      // For network errors, keep the token - user might just have spotty connection
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Skip fetchUser if we just logged in (login already set user data)
      if (justLoggedIn.current) {
        justLoggedIn.current = false;
        setLoading(false);
      } else {
        fetchUser();
      }
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user_type, user_id, name } = response.data;

    localStorage.setItem('nutriquimia-token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    justLoggedIn.current = true;
    setUser({ id: user_id, email, name, user_type });
    setToken(newToken);

    // Fetch full user profile in background (non-blocking)
    axios.get(`${API}/auth/me`).then(res => {
      setUser(res.data);
    }).catch(() => {
      // Keep partial user data if /me fails - login already succeeded
    });

    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user_type, user_id, name } = response.data;

    localStorage.setItem('nutriquimia-token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    justLoggedIn.current = true;
    setUser({ id: user_id, email: userData.email, name, user_type });
    setToken(newToken);

    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('nutriquimia-token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateUser,
      isAuthenticated: !!token && !!user,
      isAdmin: user?.user_type === 'admin',
      isDistributor: user?.user_type === 'distributor',
      isClient: user?.user_type === 'client'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
